import { events } from '@/app/events/events';
import { CursorMode, inlineEditorKeyboard } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorKeyboard';
import { inlineEditorMonaco } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorMonaco';
import { LINE_HEIGHT } from '@/app/web-workers/renderWebWorker/worker/cellsLabel/CellLabel';
import { atom } from 'jotai';

export interface InlineEditorState {
  visible: boolean;
  formula: boolean;
  readOnly: boolean;
  left: number;
  top: number;
  width: number;
  height: number;
  editMode: boolean;
  // True when the user has scrolled the viewport away from where editing
  // started, so the editor is pinned to its original screen position (detached
  // from its cell). Used to show the cell-reference label above the editor.
  detached: boolean;
}

export const defaultInlineEditor: InlineEditorState = {
  visible: false,
  formula: false,
  readOnly: false,
  left: 0,
  top: 0,
  width: 0,
  height: LINE_HEIGHT,
  editMode: false,
  detached: false,
};

const inlineEditorBaseAtom = atom<InlineEditorState>(defaultInlineEditor);

// WKWebView occasionally drops the first programmatic focus after the editor
// becomes visible, so we retry over a handful of animation frames. Five frames
// (~80ms at 60fps) is empirically enough for WKWebView to settle without a
// user-perceptible delay; bump it if focus still flakes on slower devices.
//
// Only one inline editor is ever mounted at a time, so a single module-level
// handle is sufficient. It's tracked so the retry chain can be cancelled if the
// editor hides before it fires (otherwise a late focus could steal focus from
// whatever is now active).
const FOCUS_RETRY_FRAMES = 5;
let focusRetryHandle: number | null = null;

const cancelFocusRetries = () => {
  if (focusRetryHandle !== null) {
    cancelAnimationFrame(focusRetryHandle);
    focusRetryHandle = null;
  }
};

const scheduleMonacoFocus = (isVisible: () => boolean) => {
  cancelFocusRetries();
  let attempt = 0;
  const tryFocus = () => {
    focusRetryHandle = null;
    // Bail out if the editor was hidden between frames.
    if (!isVisible()) return;
    inlineEditorMonaco.focus();
    if (++attempt < FOCUS_RETRY_FRAMES) {
      focusRetryHandle = requestAnimationFrame(tryFocus);
    }
  };
  focusRetryHandle = requestAnimationFrame(tryFocus);
};

export const inlineEditorAtom = atom(
  (get) => get(inlineEditorBaseAtom),
  (get, set, newValue: InlineEditorState) => {
    const oldValue = get(inlineEditorBaseAtom);
    set(inlineEditorBaseAtom, newValue);

    // Focus the Monaco editor when becoming visible, after React has rendered.
    if (newValue.visible && !oldValue.visible) {
      scheduleMonacoFocus(() => get(inlineEditorBaseAtom).visible);
    } else if (!newValue.visible) {
      cancelFocusRetries();
    }
    inlineEditorKeyboard.cursorMode = newValue.editMode ? CursorMode.Edit : CursorMode.Enter;
    events.emit('setDirty', { cursor: true });
  }
);
